import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dialog } from '@/shared/ui';

import type { HistoryLine } from './describe';
import styles from './History.module.css';

interface Props {
  title: string;
  line: HistoryLine;
  onClose: () => void;
}

/**
 * The full before / after of a long value, exactly as stored: plain text with its line breaks
 * (Markdown stays source - it is never turned into HTML here), each side copyable.
 */
export function HistoryValueDialog({ title, line, onClose }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<'before' | 'after' | null>(null);
  const copy = async (side: 'before' | 'after') => {
    try {
      await navigator.clipboard.writeText((side === 'before' ? line.before : line.after) ?? '');
      setCopied(side);
    } catch {
      setCopied(null);
    }
  };
  const side = (name: 'before' | 'after', value: string | undefined) =>
    value === undefined ? null : (
      <section className={styles.side} aria-label={t(`databases.history.${name}`)}>
        <h3 className={styles.sideHeading}>{t(`databases.history.${name}`)}</h3>
        <pre className={styles.source} data-testid={`history-${name}`}>
          {value}
        </pre>
        <Button type="button" variant="ghost" onClick={() => void copy(name)}>
          {copied === name ? t('databases.history.copied') : t('databases.history.copy')}
        </Button>
      </section>
    );
  return (
    <Dialog title={t('databases.history.detailTitle')} size="lg" onClose={onClose}>
      <div className={styles.detail}>
        <p className={styles.muted}>{title}</p>
        {line.label && <p className={styles.label}>{line.label}</p>}
        {side('before', line.before)}
        {side('after', line.after)}
        {line.text !== undefined && <pre className={styles.source}>{line.text}</pre>}
      </div>
    </Dialog>
  );
}
